<template>
  <div>
    <t-modal-file ref="openModalUploadCSV">
         <h3 class="font-bold mb-0 px-2 text-base text-gray-700"> Import CSV </h3>
       
      <div class="flex justify-start w-full px-0 pt-0 py-3">
        <input 
          type="file" 
          ref="fileInput" 
          class="hidden" 
          accept=".csv" 
          @change="handleFiles" 
        />
        
        <div class="mr-2.5 w-full" v-if="file === null">
          <div v-if="isErrorValidate" class="bg-red-50 text-red-700 py-2.5 px-3 rounded-lg mb-3">
             <i class="fa-solid fa-circle-exclamation"></i> 
             <label class="text-sm ml-2 font-semibold">{{ validateObject.nameError }}</label>
             <p class="mb-0 text-xs">{{ validateObject.description }}</p>
          </div>
          
          <div
            class="bg-gray-50 h-40 rounded-xl dropzone-style justify-center items-center flex border-t border-l border-b border-r border-dashed border-gray-300"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="handleDrop"
          >
            <div class="dropzone-content flex justify-center items-center flex-col">
              <div class="icon mb-2">
                <svg class="w-6 h-6 text-gray-400 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v9m-5 0H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2M8 9l4-5 4 5m1 8h.01"/>
                </svg>
              </div>
              <p class="text-sm text-gray-500 m-0">Drag and drop here</p>
              <p 
                class="text-xs text-blue-500 cursor-pointer my-1 m-0"
                @click.prevent="triggerFile"
                :class="{ 'opacity-50 bg-gray-500': disabled }"
              >
                or choose from computer
              </p>
            </div>
          </div>
        <p class="text-sm text-gray-500 mt-3 mb-0">
  Please ensure your file matches the template format 
  <a 
    href="https://ebooks.gramedia.com/ebook-statics/bulk-uploader/Template+Smartpin.csv" 
    target="_blank"
    rel="noopener noreferrer"
    download
    class="not-italic cursor-pointer text-blue-500 hover:underline"
  >
    Download template sample
  </a>
</p>
        </div>

        <div v-else class="ml-2 pt-0 w-full">
          <ProgressSection 
            v-if="isLoadingProgress" 
            :progress="progress" 
            :fileName="fileName" 
            :disabled="!file"
            @generate="importCSV" 
          />
          <div v-else class="flex justify-start items-center border rounded-lg w-full py-2 px-3">
            <div class="flex-col w-full">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-left mb-0 text-sm text-gray-700 font-semibold">{{ fileName }}</p>
                  <p class="text-left text-xs text-gray-500 mb-0">{{ readableFileSize(fileSize) }}</p>
                </div>
                <div>
                  <Button @click.prevent="triggerFile" variant="outlined" color="alternative" size="xs">
                    Change
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="!isLoadingProgress" class="flex justify-start items-start space-x-4">
        <Button
          @click.prevent="importCSV"
          class="flex justify-items-start items-center text-white"
          variant="default"
          :class="file ? 'bg-blue-500' : 'bg-gray-300 text-white'"
          :color="file ? 'primary' : 'alternative'"
          size="xs"
        >
          Import
        </Button>
        <Button
          @click="closeUploadModal"
          class="flex justify-items-start items-center"
          variant="outlined"
          color="alternative"
          size="xs"
        >
          Cancel
        </Button>
      </div>
    </t-modal-file>

    <t-modal-table-content ref="openTableGenerateCSV">
      <div class="csv-review-modal flex flex-col p-2 mx-auto">
        <div class="flex justify-between items-center mb-0">
          <h3 class="font-bold mb-3 text-base text-gray-700">Review Selected Content</h3>
        </div>
      <div class="relative w-96 mb-4">
  <i class="fa-solid fa-magnifying-glass text-gray-400 text-base absolute top-3 left-3"></i>
  <input 
    placeholder="Search book name or ID..." 
    class="hide-native-search p-2.5 pl-10 pr-10 w-full text-sm border rounded-lg focus:ring-blue-500 focus:border-blue-500"
    type="text" 
    v-model="searchQuery"
  />
  <i 
    v-if="searchQuery" 
    @click="clearSearch" 
    class="fa-solid fa-xmark text-gray-400 text-base absolute top-3 right-3 cursor-pointer hover:text-gray-600"
  ></i>
</div>
        <div v-if="!baseData.is_valid" class="bg-orange-50 p-2 rounded-lg mb-4 text-yellow-800">
            <label class="text-base font-semibold mb-0 leading-10">
            <i class="fa-solid fa-circle-info mr-2"></i>  Confirmation Content
            </label>
            <p class="tex-sm mb-2">Please review the content for the Onsite feature again. If there are any errors, you won't be able to save it. Make sure to correct them first.</p>
        </div>
        <div class="csv-review-table border rounded-lg">
          <table class="min-w-full bg-white">
            <thead class="sticky top-0 z-10 bg-gray-50 border-b">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Image</th>
                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Book name</th>
                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reason</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="(item, index) in filteredData" :key="index" class="hover:bg-gray-50">
                <td class="px-6 py-1 whitespace-nowrap text-sm text-gray-500">
                    <img v-if="item.thumb_image_normal" class="h-10 w-10 object-cover rounded-none " :src="'https://ebooks.gramedia.com/ebook-covers/' + removeBucket(item.thumb_image_normal)" >
              
                  <img v-else :src="DefaultImage" class="h-10 w-10 object-cover rounded-none" alt="empty thumb" />
                </td>
                <td class="px-6 py-1 whitespace-nowrap text-sm text-gray-700">
                  {{ item.name || '-' }}
                  <p>ID: {{item.id}}</p>
                </td>
                <td class="px-6 py-1 whitespace-nowrap text-sm text-gray-500 capitalize">{{ item.type || '-' }}</td>
                 <td class="px-6 py-1 whitespace-nowrap text-sm text-gray-500 capitalize">
                  <span class="py-1 font-normal rounded-lg text-xs px-1.5" :class="item.is_valid ?'bg-blue-100 text-blue-800':'bg-red-100 text-red-800'">
                    {{item.is_valid ? 'Passed' : 'Failed'}}
                  </span>
                 </td>
                <td class="px-6 py-1  w-full align-middle whitespace-normal text-sm text-gray-500 capitalize max-w-xs">
  <p class="text-sm break-words mb-0">
      {{ item.message || '' }}
  </p>
</td>
              </tr>
              <tr v-if="filteredData.length === 0">
                <td colspan="5" class="px-6 py-1 text-center text-sm text-gray-500">No data available.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex flex-col justify-start items-start" v-if="baseData.is_valid">
          <div class="space-x-3">
              <Button class="mt-4 text-gray-700" @click="saveItem"  size="xs">
          Save
        </Button>
           <Button class="mt-4 text-gray-700" @click="backToUpload"  variant="outlined" color="alternative" size="xs">
          Cancel
        </Button>
          </div>
        </div>
        <div v-else>
            <Button class="mt-4 text-gray-700" @click="backToUpload" variant="outlined" color="alternative" size="xs">
          Back to Choose Again
        </Button>
        </div>
      
      </div>
    </t-modal-table-content>
  </div>
</template>

<script>
import Button from '@/components/ui/Button'
import { mapActions } from 'vuex';

import XLSX from 'xlsx'
import { EventBus } from '../../event-bus.js'
import ProgressSection from '../../components/smart-pin/progress-bar.vue'
import DefaultImage from '../../assets/img/emptyitem.png'
export default {
  name: 'CsvImportModal',
  components: {
    Button,
    ProgressSection,
  },
  props: {
   baseData: {
    type: [Array, Object],
    default: () => [] 
  }
  },
  data() {
    return {
      file: null,
      fileName: '',
      fileSize: null,
      disabled: false,
      isErrorValidate: false,
      searchQuery:'',
      validateObject: {},
      DefaultImage: DefaultImage,
      isLoadingProgress: false,
      progress: 0,
      csvDataRaw: [],
      mergedData: [],
      intervalId: null
    };
  },
  computed: {
    filteredData() {
      const sourceData = this.baseData?.data || [];

      if (!this.searchQuery) {
        return sourceData;
      }

      const query = this.searchQuery.toLowerCase();
      
      return sourceData.filter(item => {
        const name = (item.name || '').toLowerCase();
        const id = String(item.id || '').toLowerCase();
        
        return name.includes(query) || id.includes(query);
      });
    }
  },
  methods: {
     ...mapActions('smartpin', ['addItemLocation']),
    openModal() {
      this.resetState();
      this.$refs.openModalUploadCSV.show();
    },
    
    closeUploadModal() {
      if (this.file) {
        this.file = null;
      } else {
        this.$refs.openModalUploadCSV.hide();
      }
    },

    backToUpload() {
      this.$refs.openTableGenerateCSV.hide();
      this.resetState();
      this.$refs.openModalUploadCSV.show();
    },
   async saveItem() {
      const csvIds = this.csvDataRaw.map(row => Number(row.item_id));
      
      const payload = {
        id: this.$route.params.id,
        type: "add",
        item_ids: csvIds
      };

      try {
        const result = await this.addItemLocation(payload);
        console.log("Berhasil menyimpan item:", result);
        this.$refs.openModalUploadCSV.hide();
        this.$refs.openTableGenerateCSV.hide();
          EventBus.$emit('show-toast', {
          message: 'Content has been successfully saved.',
          type: 'success'
        });
        this.$emit('on-save-success');
      this.resetState();
      } catch (error) {
        console.error("Gagal saat mencoba saveItem (Komponen):", error);
        
      }
    },
    clearSearch() {
      this.searchQuery = '';
    },
     removeBucket(str) {
      if (str && str.substring(0, 6) == "images") {
        return str.substring(9);
      } else {
        return str ?? 'default.png';
      }
    },
    resetState() {
      this.file = null;
      this.fileName = '';
      this.fileSize = null;
      this.isErrorValidate = false;
      this.progress = 0;
      this.csvDataRaw = [];
      this.mergedData = [];
    },

    triggerFile() {
      this.$refs.fileInput.click();
    },

    handleFiles(e) {
      const file = e?.target?.files?.[0];
      this.processSelectedFile(file);
      e.target.value = null;
    },

    handleDrop(event) {
      const file = event.dataTransfer?.files?.[0];
      this.processSelectedFile(file);
    },

    processSelectedFile(file) {
      if (!file) return;
      if (!file.name.endsWith('.csv')) {
        alert('Invalid file format. Please upload a .csv file.');
        return;
      }
      this.file = file;
      this.fileName = file.name;
      this.fileSize = file.size;
      this.isErrorValidate = false;
    },

    readableFileSize(size) {
      if (!size) return '0 KB';
      const kb = size / 1024;
      return kb > 1024 ? (kb / 1024).toFixed(2) + ' MB' : kb.toFixed(2) + ' KB';
    },

    importCSV() {
      if (!this.file) return;
      
      this.isLoadingProgress = true;
      const reader = new FileReader();
      reader.readAsArrayBuffer(this.file);

      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          let csvData = XLSX.utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            { raw: true, defval: null }
          );

          if (csvData.length === 0) {
            this.showError('Empty File', 'The uploaded CSV file does not contain any valid content rows');
            return;
          }

          const headers = Object.keys(csvData[0]);
          if (headers.length !== 1 || headers[0] !== 'item_id') {
            this.showError('File Does Not Match Template', 'Please ensure the file is filled out according to the template example and then re-import.');
            return;
          }

          const maxRows = 100;
          if (csvData.length > maxRows) {
            this.showError('Batch Limit Exceeded', `You can only upload a maximum of ${maxRows} items per CSV batch`);
            return;
          }

          this.csvDataRaw = csvData;
          this.progressLoading();
        } catch (err) {
          console.error(err);
          EventBus.$emit('show-toast', { message: 'Terjadi kesalahan saat membaca file CSV.', type: 'error' });
          this.isLoadingProgress = false;
        }
      }
    },

    showError(nameError, description) {
      this.isErrorValidate = true;
      this.validateObject = { nameError, description };
      this.file = null;
      this.fileName = '';
      this.fileSize = null;
      this.isLoadingProgress = false;
    },
    progressLoading() {
      const total = this.csvDataRaw.length || 1;
      let current = 0;
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (current < total) {
          current++;
          this.progress = Math.floor((current / total) * 100);
        } else {
          clearInterval(this.intervalId);
          this.$refs.openModalUploadCSV.hide();
          this.isLoadingProgress = false;
          this.progress = 0;
          this.processCsvData();
          this.$refs.openTableGenerateCSV.show();
        }
      }, 300);
    },

    processCsvData() {
      const csvIds = this.csvDataRaw.map(row => Number(row.item_id));
      this.$emit('item-ids-data', csvIds)
    }
  }
}
</script>

<style scoped>
.csv-review-modal {
  max-height: 850px;
  overflow:hidden;
}

.csv-review-table {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
</style>
